import { Component, AfterViewInit } from '@angular/core';
import { FilterFieldComponent } from '../filter-field.component';
import { RangeFilterField } from './filter-field-range';
import { UntypedFormControl, Validators, AbstractControl } from '@angular/forms';

@Component({
  selector: 'va-filter-range',
  template: `
    <div class="range-container">
      <mat-form-field class="range-value-container">
        <input
          type="number"
          matInput
          [formControl]="minValueFormControl"
          [ngModel]="filterField.minimum"
          [min]="filterField.bounds.minimum"
          [max]="filterField.bounds.maximum"
          (keydown)="handleKeyDown($event)"
          (blur)="updateRange()"
          />
        <span matSuffix>{{ filterField.suffix }}</span>
      </mat-form-field>
      to
      <mat-form-field class="range-value-container">
        <input
          type="number"
          matInput
          [formControl]="maxValueFormControl"
          [ngModel]="filterField.maximum"
          [min]="filterField.bounds.minimum"
          [max]="filterField.bounds.maximum"
          (keydown)="handleKeyDown($event)"
          (blur)="updateRange()"
          />
        <span matSuffix>{{ filterField.suffix }}</span>
      </mat-form-field>
    </div>
    @if (minValueFormControl.hasError('required')) {
      <mat-error>Minimum is required</mat-error>
    }
    @if (maxValueFormControl.hasError('required')) {
      <mat-error>Maximum is required</mat-error>
    }
    @if (minValueFormControl.hasError('pattern')) {
      <mat-error>Minimum must be a number</mat-error>
    }
    @if (maxValueFormControl.hasError('pattern')) {
      <mat-error>Maximum must be a number</mat-error>
    }
    @if (minValueFormControl.hasError('bounds')) {
      <mat-error>
        Minimum must be from {{ filterField.bounds.minimum }} to {{ filterField.bounds.maximum }}.
      </mat-error>
    }
    @if (maxValueFormControl.hasError('bounds')) {
      <mat-error>
        Maximum must be from {{ filterField.bounds.minimum }} to {{ filterField.bounds.maximum }}.
      </mat-error>
    }
    @if (
      (minValueFormControl.hasError('minMax') || maxValueFormControl.hasError('minMax')) &&
      !minValueFormControl.hasError('required') &&
      !maxValueFormControl.hasError('required') &&
      !minValueFormControl.hasError('pattern') &&
      !maxValueFormControl.hasError('pattern')
      ) {
      <mat-error
        >
        Minimum must be less than or equal to the maximum
      </mat-error>
    }
    `,
  styles: [
    `
      .range-container {
        width: 100%;
        display: flex;
        justify-content: space-around;
        align-items: center;
        min-width: 198px;
        color: rgba(0, 0, 0, 0.54);
      }
    `,
    `
      .range-value-container {
        width: 55px;
      }
    `,
    `
      input {
        margin: 0;
        padding: 0;
        border: 0;
        vertical-align: initial;
      }
    `,
  ],
  standalone: false,
})
export class RangeFilterFieldComponent extends FilterFieldComponent<RangeFilterField> implements AfterViewInit {
  private validators = [
    Validators.required,
    Validators.pattern('^-?\\d+$'),
    this.minMaxValidator.bind(this),
    this.boundsValidator.bind(this),
  ];

  minValueFormControl = new UntypedFormControl('', this.validators);
  maxValueFormControl = new UntypedFormControl('', this.validators);

  ngAfterViewInit(): void {
    this.revalidateForm();
  }

  updateRange(): void {
    this.revalidateForm();
    if (
      !this.isValid() ||
      (this.filterField.minimum === this.minValueFormControl.value &&
        this.filterField.maximum === this.maxValueFormControl.value)
    ) {
      return;
    }
    this.filterField.value = {
      minimum: this.minValueFormControl.value,
      maximum: this.maxValueFormControl.value,
    };
    this.applyFilter(this.filterField.value);
  }

  handleKeyDown($event: KeyboardEvent): void {
    if ($event.keyCode === 13) {
      this.updateRange();
    }
  }

  private revalidateForm(): void {
    this.minValueFormControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    this.maxValueFormControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  private isValid(): boolean {
    return !this.maxValueFormControl.invalid && !this.minValueFormControl.invalid;
  }

  private minMaxValidator(control: AbstractControl): { [key: string]: any } | null {
    let valid = true;
    if (!!this.minValueFormControl && !!this.maxValueFormControl) {
      valid = this.minValueFormControl.value <= this.maxValueFormControl.value;
    }
    return valid ? null : { minMax: { value: control.value } };
  }

  private boundsValidator(control: AbstractControl): { [key: string]: any } | null {
    let valid = true;
    if (!!this.minValueFormControl && !!this.maxValueFormControl) {
      valid =
        this.minValueFormControl.value >= this.filterField.bounds.minimum &&
        this.maxValueFormControl.value <= this.filterField.bounds.maximum;
    }
    return valid ? null : { bounds: { value: control.value } };
  }
}
